import axios, { AxiosError, type AxiosInstance } from 'axios'
import { resolveLocale, translate } from './i18n'

/**
 * The response envelope every endpoint returns (SUAP-0005):
 *
 *   { data, meta, error }
 *
 * `error.code` is a stable machine-readable string — branch on it, never on the
 * message, which is localised and may change.
 */
export interface Envelope<T> {
  data?: T
  meta?: PageMeta
  error?: ApiErrorBody
}

export interface PageMeta {
  page: number
  limit: number
  total: number
  total_pages: number
}

export interface ApiErrorBody {
  code: string
  message: string
  details?: Record<string, string>
  request_id?: string
}

/** A page of results, with its meta kept alongside. */
export interface Page<T> {
  items: T
  meta: PageMeta | undefined
}

/**
 * ApiError is what every failed call rejects with, whatever went wrong.
 *
 * A network failure and a 500 both arrive here with a code, so callers never
 * have to tell an axios error from an envelope error.
 */
export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details: Record<string, string>
  readonly requestId: string | undefined

  constructor(body: ApiErrorBody, status: number) {
    super(body.message)
    this.name = 'ApiError'
    this.code = body.code
    this.status = status
    this.details = body.details ?? {}
    this.requestId = body.request_id
  }

  /** The message for a specific field, when the backend marked one. */
  fieldError(field: string): string | undefined {
    return this.details[field]
  }

  /** The session is gone — sign in again (SUAP-0003). */
  get isAuthFailure(): boolean {
    return this.code === 'unauthorized' || this.code === 'token_revoked' || this.code === 'token_expired'
  }
}

const TOKEN_KEY = 'suap.token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string | null): void {
  if (token === null) localStorage.removeItem(TOKEN_KEY)
  else localStorage.setItem(TOKEN_KEY, token)
}

/** Called when a request comes back with a dead session. SUAP-0202 wires it. */
let onAuthFailure: (() => void) | null = null

export function setAuthFailureHandler(handler: (() => void) | null): void {
  onAuthFailure = handler
}

export const http: AxiosInstance = axios.create({
  baseURL: '/',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
})

http.interceptors.request.use((config) => {
  const token = getToken()
  if (token !== null) config.headers.Authorization = `Bearer ${token}`
  // The backend localises its messages; tell it which language to use.
  config.headers['X-Suap-Language'] = document.documentElement.lang || 'id'
  return config
})

function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error

  const axiosError = error as AxiosError<Envelope<unknown>>
  const body = axiosError.response?.data?.error

  if (body !== undefined) {
    return new ApiError(body, axiosError.response?.status ?? 0)
  }

  // No envelope: the request never reached a handler — offline, DNS, timeout, or
  // an ingress error page. Give it a code of its own so callers can say
  // "you appear to be offline" rather than "something went wrong".
  const offline = axiosError.code === 'ERR_NETWORK' || axiosError.code === 'ECONNABORTED'
  return new ApiError(
    {
      code: offline ? 'network_unavailable' : 'internal_error',
      message: offline
        ? translate(resolveLocale(), 'error.offline')
        : translate(resolveLocale(), 'error.server'),
    },
    axiosError.response?.status ?? 0,
  )
}

/**
 * unwrap turns an enveloped response into the payload, and any failure into an
 * ApiError. Callers see `T`, never `{data}`.
 */
async function unwrap<T>(request: Promise<{ data: Envelope<T>; status: number }>): Promise<T> {
  try {
    const response = await request
    const body = response.data

    if (body.error !== undefined) {
      throw new ApiError(body.error, response.status)
    }
    if (body.data === undefined) {
      throw new ApiError({ code: 'internal_error', message: translate(resolveLocale(), 'error.emptyResponse') }, response.status)
    }
    return body.data
  } catch (error) {
    const apiError = toApiError(error)
    if (apiError.isAuthFailure) onAuthFailure?.()
    throw apiError
  }
}

export const api = {
  get: <T>(url: string, params?: Record<string, unknown>): Promise<T> =>
    unwrap<T>(http.get<Envelope<T>>(url, { params })),

  post: <T>(url: string, body?: unknown): Promise<T> =>
    unwrap<T>(http.post<Envelope<T>>(url, body)),

  put: <T>(url: string, body?: unknown): Promise<T> =>
    unwrap<T>(http.put<Envelope<T>>(url, body)),

  patch: <T>(url: string, body?: unknown): Promise<T> =>
    unwrap<T>(http.patch<Envelope<T>>(url, body)),

  delete: <T>(url: string): Promise<T> =>
    unwrap<T>(http.delete<Envelope<T>>(url)),

  /** A list call, keeping the pagination meta the caller needs to page. */
  list: async <T>(url: string, params?: Record<string, unknown>): Promise<Page<T>> => {
    const response = await http.get<Envelope<T>>(url, { params })
    const body = response.data

    if (body.error !== undefined) {
      const apiError = new ApiError(body.error, response.status)
      if (apiError.isAuthFailure) onAuthFailure?.()
      throw apiError
    }

    return { items: body.data as T, meta: body.meta }
  },
}
